import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const directOrder = params.get("order");
  const { clear } = useCart();
  const [status, setStatus] = useState(directOrder ? "paid" : "checking");
  const [orderId, setOrderId] = useState(directOrder || null);
  const [kind, setKind] = useState("order");
  const cleared = useRef(false);

  useEffect(() => {
    if (directOrder) {
      if (!cleared.current) { clear(); cleared.current = true; }
      return;
    }
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setKind(data.kind);
        setOrderId(data.order_id);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { setStatus("error"); return; }
      if (attempts++ < 12) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, directOrder]);

  return (
    <Layout>
      <div className="max-w-xl mx-auto px-4 py-32 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="h-12 w-12 text-gold animate-spin mx-auto" />
            <h1 className="mt-6 font-serif text-3xl text-charcoal">Confirming your payment…</h1>
            <p className="mt-3 text-charcoal/60">Just a moment while we finish things up.</p>
          </>
        )}
        {status === "paid" && (
          <div className="fade-up" data-testid="payment-success">
            <CheckCircle2 className="h-16 w-16 text-gold mx-auto" />
            <h1 className="mt-6 font-serif text-4xl text-charcoal">
              {kind === "giftcard" ? "Gift card on its way!" : "Thank you for your order!"}
            </h1>
            <p className="mt-4 text-charcoal/70">
              {kind === "giftcard"
                ? "Your digital gift card has been created and emailed. Enjoy gifting! 🎁"
                : "Your order is confirmed and being wrapped with love. A confirmation email is on its way."}
            </p>
            {orderId && kind !== "giftcard" && (
              <p className="mt-3 eyebrow">Order #{orderId}</p>
            )}
            <div className="mt-8 flex justify-center gap-3">
              <Button asChild className="bg-charcoal text-white rounded-none hover:bg-gold font-display tracking-wide">
                <Link to="/shop" data-testid="success-continue-btn">Continue Shopping</Link>
              </Button>
              {kind !== "giftcard" && (
                <Button asChild variant="outline" className="rounded-none border-charcoal font-display tracking-wide">
                  <Link to="/account">View Orders</Link>
                </Button>
              )}
            </div>
          </div>
        )}
        {["error", "timeout"].includes(status) && (
          <>
            <h1 className="font-serif text-3xl text-charcoal">We couldn't confirm your payment</h1>
            <p className="mt-3 text-charcoal/60">
              If you were charged, your order will still be processed. Please check your account or contact info@luvsprout.com.
            </p>
            <Button asChild className="mt-8 bg-charcoal text-white rounded-none hover:bg-gold font-display">
              <Link to="/">Back to Home</Link>
            </Button>
          </>
        )}
      </div>
    </Layout>
  );
}
